import {
    CombinedMissionStatus,
    DisplayableMissionDocumentation,
    DisplayableMissionDto,
    findActiveApprovals,
    FormResponseFormGroup,
    FormResponsesWithStep,
    InUseMissionWorkflowVersionDto,
    prefillPreviousDocumentation,
    RequestersMissionApprovalDto,
    SimpleAuthorityDto
} from '@flyfreely-portal-ui/flyfreely';
import moment from 'moment';

export enum SubmitOption {
    NONE = 'NONE',
    SUBMIT = 'SUBMIT',
    FLY = 'FLY'
}

/**
 * Determine if the workflow in the saved/initial mission exists, or
 * now has a different version, or is missing.
 *
 * @param mission the current saved, or initial mission
 * @param workflowList the available workflows
 */
export function findWorkflowSelectionValidity(
    mission: DisplayableMissionDto | null,
    workflowList: InUseMissionWorkflowVersionDto[]
): 'VALID' | 'MISSING' | 'DIFFERENT_VERSION' {
    if (mission == null || mission.missionWorkflowVersion == null) {
        return 'VALID';
    }
    const existingWorkflow = mission.missionWorkflowVersion;

    const workflow = workflowList.find(
        w => w.workflowId === existingWorkflow.workflowId
    );
    if (workflow == null) {
        return 'MISSING';
    }

    return workflow.id === existingWorkflow.id ? 'VALID' : 'DIFFERENT_VERSION';
}

const RECREATABLE_APPROVAL_STATUSES: RequestersMissionApprovalDto.Status[] = [
    RequestersMissionApprovalDto.Status.CANCELLED,
    RequestersMissionApprovalDto.Status.REVOKED,
    RequestersMissionApprovalDto.Status.REJECTED
];

/**
 * Find the approvals on the mission that have been cancelled, revoked or
 * rejected, and so need a new approval to be created for their authority.
 *
 * An approval qualifies when its authority still requires approval and is
 * still part of the mission plan, either via the mission workflow or as an
 * additional authority on the mission, and it is the most recently created
 * approval for that authority. Any newer approval for the same authority
 * supersedes it.
 *
 * @param mission the saved mission
 * @returns the latest approval of each authority that needs a new approval
 */
export function findApprovalsRequiringRecreation(
    mission: DisplayableMissionDto | null
): RequestersMissionApprovalDto[] {
    if (mission == null || (mission.approvals ?? []).length === 0) {
        return [];
    }

    const workflowVersion = mission.missionWorkflowVersion;
    const missionAuthorities = [
        workflowVersion?.delegatedAuthority,
        ...(workflowVersion?.additionalAuthorities ?? []),
        ...(mission.additionalAuthorities ?? [])
    ]
        .filter(
            (authority): authority is SimpleAuthorityDto =>
                authority?.requiresApproval === true
        )
        .filter(
            (authority, index, all) =>
                all.findIndex(a => a.id === authority.id) === index
        );

    return missionAuthorities
        .map(authority =>
            mission.approvals
                .filter(approval => approval.authorityId === authority.id)
                .reduce<RequestersMissionApprovalDto | null>(
                    (latest, approval) =>
                        latest == null ||
                        moment(approval.creationTime).isAfter(
                            moment(latest.creationTime)
                        )
                            ? approval
                            : latest,
                    null
                )
        )
        .filter(
            (approval): approval is RequestersMissionApprovalDto =>
                approval != null &&
                RECREATABLE_APPROVAL_STATUSES.includes(approval.status)
        );
}

/**
 * Find the cancelled, revoked or rejected approvals that the current user
 * should create a new approval for now.
 *
 * Only users who can edit the mission recreate approvals, so an approver who
 * revokes or rejects an approval doesn't create its replacement. Any that are
 * skipped are created the next time someone who can edit the mission opens it.
 *
 * @param mission the saved mission
 * @param combinedStatus the mission's combined status
 * @param recreatedApprovalIds the approvals that have already been recreated
 * @returns the approvals to create a new approval for
 */
export function findApprovalsToRecreate(
    mission: DisplayableMissionDto | null,
    combinedStatus: CombinedMissionStatus,
    recreatedApprovalIds: ReadonlySet<number>
): RequestersMissionApprovalDto[] {
    if (
        mission?.id == null ||
        !mission.availableActions?.canEdit ||
        (combinedStatus !== CombinedMissionStatus.DRAFT &&
            combinedStatus !== CombinedMissionStatus.SUBMITTED)
    ) {
        return [];
    }
    return findApprovalsRequiringRecreation(mission).filter(
        approval => !recreatedApprovalIds.has(approval.id)
    );
}

/**
 * A draft approval that was created to replace an approval of the same
 * authority that had been cancelled, revoked or rejected.
 */
export interface RecreatedApproval {
    approval: RequestersMissionApprovalDto;
    previousApproval: RequestersMissionApprovalDto;
}

/**
 * Find the draft approvals on the mission that replace a cancelled, revoked or
 * rejected approval, along with the approval each one replaces.
 *
 * A draft approval has been recreated when the approval created before it for
 * the same authority was cancelled, revoked or rejected. Draft approvals that
 * are the first for their authority have never been submitted, and so aren't
 * included.
 *
 * @param mission the saved mission
 * @returns the recreated draft approvals and the approvals they replace
 */
export function findRecreatedDraftApprovals(
    mission: DisplayableMissionDto | null
): RecreatedApproval[] {
    const approvals = mission?.approvals ?? [];
    return approvals
        .filter(
            approval =>
                approval.status === RequestersMissionApprovalDto.Status.DRAFT
        )
        .map(approval => ({
            approval,
            previousApproval: approvals
                .filter(
                    previous =>
                        previous.authorityId === approval.authorityId &&
                        moment(previous.creationTime).isBefore(
                            moment(approval.creationTime)
                        )
                )
                .reduce<RequestersMissionApprovalDto | null>(
                    (latest, previous) =>
                        latest == null ||
                        moment(previous.creationTime).isAfter(
                            moment(latest.creationTime)
                        )
                            ? previous
                            : latest,
                    null
                )
        }))
        .filter(
            (recreated): recreated is RecreatedApproval =>
                recreated.previousApproval != null &&
                RECREATABLE_APPROVAL_STATUSES.includes(
                    recreated.previousApproval.status
                )
        );
}

const APPROVAL_REQUIRING_ENTITY_TYPE = 'MissionApproval';

/**
 * Keep the completion of the response that a prefilled response was copied
 * from, when it was copied as is.
 *
 * Prefilled responses start off incomplete, so that they're reviewed before
 * being submitted. A recreated approval's documentation can't be reviewed
 * while the mission is locked by its other approvals, and its documentation
 * is only valid once its forms are complete, so a copy of a completed
 * response stays complete. A response that was mapped from an older version of
 * the form still needs to be reviewed.
 *
 * @param prefilled the prefilled response
 * @param previous the response it was copied from
 */
function keepCompletion(
    prefilled: FormResponsesWithStep,
    previous: FormResponsesWithStep | undefined
): FormResponsesWithStep {
    return previous != null &&
        previous.formVersionId === prefilled.formVersionId
        ? { ...prefilled, completed: previous.completed }
        : prefilled;
}

/**
 * Prefill the pre-submit documentation of recreated approvals from the
 * responses to the approvals they replace, in the same way that a cloned
 * mission's documentation is prefilled from its source mission.
 *
 * The responses of every approval on the mission are stored together, grouped
 * by form, so they are narrowed down to those of each approval before being
 * prefilled. Forms that the recreated approval already has responses for are
 * left alone.
 *
 * @param documentation the mission's current documentation
 * @param recreatedApprovals the recreated approvals to prefill
 * @returns the new responses to store, and the forms that couldn't be
 * prefilled because their form version has changed
 */
export function prefillRecreatedApprovalDocumentation(
    documentation: DisplayableMissionDocumentation,
    recreatedApprovals: RecreatedApproval[]
): {
    formResponses: FormResponsesWithStep[];
    prefillErrors: { [formId: number]: boolean };
} {
    const step = 'pre-submit';
    const approvalResponses = (
        approval: RequestersMissionApprovalDto
    ): FormResponseFormGroup =>
        Object.keys(documentation.formResponses).reduce(
            (acc, formId) => ({
                ...acc,
                [formId]: documentation.formResponses[Number(formId)].filter(
                    response =>
                        response.step === step &&
                        response.requiringEntityType ===
                            APPROVAL_REQUIRING_ENTITY_TYPE &&
                        response.requiringEntityId === approval.id
                )
            }),
            {}
        );

    return recreatedApprovals.reduce(
        (acc, { approval, previousApproval }) => {
            const requiredDocumentation = {
                [step]: (documentation.requiredDocumentation[step] ?? []).map(
                    required => ({
                        ...required,
                        forms: required.forms.filter(
                            form =>
                                form.requiringEntityType ===
                                    APPROVAL_REQUIRING_ENTITY_TYPE &&
                                form.requiringEntityId === approval.id
                        )
                    })
                )
            };
            if (
                requiredDocumentation[step].every(
                    required => required.forms.length === 0
                )
            ) {
                return acc;
            }

            const currentResponses = approvalResponses(approval);
            const previousResponses = approvalResponses(previousApproval);
            const { formResponses, prefillErrors } =
                prefillPreviousDocumentation(
                    currentResponses,
                    previousResponses,
                    requiredDocumentation,
                    step
                );

            // Only the forms that have been prefilled need to be stored, the
            // rest already have responses for this approval.
            const prefilledResponses = Object.keys(formResponses)
                .map(Number)
                .filter(formId => (currentResponses[formId] ?? []).length === 0)
                .reduce<FormResponsesWithStep[]>(
                    (responses, formId) =>
                        responses.concat(
                            formResponses[formId].map(response =>
                                keepCompletion(
                                    response,
                                    previousResponses[formId]?.[0]
                                )
                            )
                        ),
                    []
                );

            return {
                formResponses: acc.formResponses.concat(prefilledResponses),
                prefillErrors: { ...acc.prefillErrors, ...prefillErrors }
            };
        },
        {
            formResponses: [] as FormResponsesWithStep[],
            prefillErrors: {} as { [formId: number]: boolean }
        }
    );
}

/**
 * Can draft approvals be submitted one at a time from the inline approval.
 *
 * While none of the mission's approvals have been submitted, they are all
 * submitted together by the mission's "Submit for Approval" button. Once an
 * approval has been cancelled, revoked or rejected and then recreated as a
 * draft, on a mission with other active approvals, each approval can be
 * submitted on its own instead, as the other approvals may already be under
 * review or approved.
 *
 * @param mission the saved mission
 */
export function canSubmitApprovalsIndividually(
    mission: DisplayableMissionDto | null
) {
    return (
        mission?.id != null &&
        findRecreatedDraftApprovals(mission).length > 0 &&
        findActiveApprovals(mission.approvals, true).length > 1
    );
}
